/**
 * Asetet — një derë e vetme drejt `/api/assets`.
 *
 * Moduli NUK kalon nëpër `apiService().get('assets')`: ai flet me CRUD-in
 * gjenerik `/api/:store`, dhe aty një gabim leximi kthehet si listë bosh
 * ("Store jo i njohur" → `{data: []}`). Për një regjistër pajisjesh kjo do të
 * thoshte se një server i rënë dhe një depo e zbrazët duken njësoj.
 *
 * Këtu çdo dështim del si `Error` me mesazhin e serverit — dhe faqja e tregon.
 */

import { authFetch } from './apiServices';

const API = process.env.REACT_APP_API_URL || '';

async function call(path, options = {}) {
  const res = await authFetch(`${API}/api${path}`, {
    ...options,
    headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok || json.success === false) {
    throw new Error(json.error || `Gabim ${res.status}`);
  }
  return json.data;
}

const qs = (params = {}) => {
  const clean = Object.entries(params).filter(([, v]) => v !== '' && v !== null && v !== undefined && v !== 'all');
  return clean.length ? `?${new URLSearchParams(Object.fromEntries(clean)).toString()}` : '';
};

const assetsService = {
  /** Regjistri i plotë, me mbajtësin aktual të secilës pajisje. */
  list:   (filters)     => call(`/assets${qs(filters)}`),
  /** Pajisja + caktimet + historiku, në një thirrje. */
  detail: (id)          => call(`/assets/${id}`),
  create: (body)        => call('/assets', { method: 'POST',   body: JSON.stringify(body) }),
  /**
   * Importi i një pjese rreshtash nga një listë e jashtme.
   *
   * Dërgohet me pjesë (`AssetsImportDialog`), jo e tëra: serveri i ndal mbi 500
   * rreshta njëherësh, dhe një kërkesë e vetme me dyqind pajisje do të mbante
   * shfletuesin pezull pa treguar asnjë përparim.
   */
  importRows: (rows, options = {}) =>
    call('/assets/import', { method: 'POST', body: JSON.stringify({ rows, options }) }),
  update: (id, body)    => call(`/assets/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  remove: (id)          => call(`/assets/${id}`, { method: 'DELETE' }),

  assign: (id, body)    => call(`/assets/${id}/assign`, { method: 'POST', body: JSON.stringify(body) }),
  ret:    (id, body)    => call(`/assets/${id}/return`, { method: 'POST', body: JSON.stringify(body) }),

  /** Dosja e aseteve e një punonjësi + paralajmërimi i largimit. */
  forWorker: (workerId) => call(`/assets/worker/${workerId}`),
  /** Vetëshërbim — pajisjet që mban vetë përdoruesi i kyçur. */
  mine:      ()         => call('/my-assets'),

  // ── Dosja ────────────────────────────────────────────────────────────────
  addService:  (id, body) => call(`/assets/${id}/services`,  { method: 'POST', body: JSON.stringify(body) }),
  addIncident: (id, body) => call(`/assets/${id}/incidents`, { method: 'POST', body: JSON.stringify(body) }),
  deleteService: (serviceId) => call(`/assets/services/${serviceId}`, { method: 'DELETE' }),

  /**
   * Ngarkimi shkon me `FormData`, ndaj NUK kalon nëpër `call()`: ai vë
   * `Content-Type: application/json`, dhe me të multer-i nuk e sheh skedarin.
   * Browser-i duhet ta vendosë vetë kufirin e `multipart`.
   */
  async uploadFile(id, file, { kind = 'document', name } = {}) {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('kind', kind);
    if (name) fd.append('name', name);
    const res = await authFetch(`${API}/api/assets/${id}/files`, { method: 'POST', body: fd });
    const json = await res.json().catch(() => ({}));
    if (!res.ok || json.success === false) throw new Error(json.error || `Gabim ${res.status}`);
    return json.data;
  },
  deleteFile: (fileId) => call(`/assets/files/${fileId}`, { method: 'DELETE' }),
  /** URL-ja e drejtpërdrejtë e një skedari (foto inline ose shkarkim). */
  fileUrl: (fileId, inline = false) => `${API}/api/assets/files/${fileId}${inline ? '?inline=1' : ''}`,

  // ── Procesverbali i dorëzim–pranimit ─────────────────────────────────────
  handovers:      (filters)  => call(`/asset-handovers${qs(filters)}`),
  handover:       (id)       => call(`/asset-handovers/${id}`),
  createHandover: (body)     => call('/asset-handovers', { method: 'POST', body: JSON.stringify(body) }),
  updateHandover: (id, body) => call(`/asset-handovers/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  confirmHandover:(id)       => call(`/asset-handovers/${id}/confirm`, { method: 'POST' }),
  deleteHandover: (id)       => call(`/asset-handovers/${id}`, { method: 'DELETE' }),
};

export default assetsService;
